import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthService, CovidAssessmentsService, QueryService, WorkspaceService } from '@pt/api-client';
import {
  CovidAssessmentDocCreateRequest,
  CovidAssessmentDocUpdateRequest,
  DocumentDuplicateResponse,
  WorkspaceItem,
  WorkspaceItemType,
  WorkspaceMoveRequest
} from '@primatoo-ms-organization';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';

import { WidgetDialogService } from '../../services/widget-dialog.service';
import * as CovidAssessmentActions from '../actions/covid-assessment.actions';
import { PtCoreActions } from '@pt/pt-core';
import { DialogService } from '../../../services';
import { TranslateService } from '@ngx-translate/core';
import { ValidateDocumentRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/ValidateDocumentRequest';
import { CreateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/CreateDocResponse';
import { UiDocumentItem } from '@pt/pt-models';
import { UpdateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpdateDocResponse';


@Injectable()
export class CovidAssessmentEffects {

  publishCovidAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CovidAssessmentActions.publishCovidAssessment),
      switchMap((action: { docId: number, skipBack?: boolean }) =>
        this.covidAssessmentService.publish(action.docId).pipe(
          switchMap(() => {
            if (action?.skipBack) {
              return [
                CovidAssessmentActions.publishCovidAssessmentSuccess(),
                PtCoreActions.showOperationSuccessNotification()
              ];
            } else {
              return [
                CovidAssessmentActions.publishCovidAssessmentSuccess(),
                PtCoreActions.navigateBack({
                  hop: -1,
                  queryParams: {
                    opened: true,
                    id: action.docId
                  }
                }),
                PtCoreActions.showOperationSuccessNotification()
              ];
            }
          }),
          catchError(err =>
            of(CovidAssessmentActions.publishCovidAssessmentFailed({ error: err.message }))
          )
        )
      )
    );
  });
  publishCovidAssessmentWithConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CovidAssessmentActions.publishCovidAssessmentWithConfirm),
      switchMap((action: { docId: number, skipBack?: boolean, hideInfo?: boolean }) => {
        const title = 'TITLE.COVID_ASSESSMENT_PUBLISH';
        const message = 'MESSAGE.COVID_ASSESSMENT_PUBLISH';
        // const info = 'MESSAGE.ON_END_OPERATION_COME_BACK';

        return this.widgetDialogService.runConfirmDialog(
          title,
          message
          // !action.hideInfo ? info : null
        ).pipe(
          map((response) => {
            if (response) {
              this.store.dispatch(CovidAssessmentActions.publishCovidAssessment({
                docId: action?.docId,
                skipBack: action?.skipBack
              }));
            }
          })
        );
      })
    ), { dispatch: false }
  );
  validateDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CovidAssessmentActions.validateCovidAssessment),
      switchMap((action: { document: UiDocumentItem, request: ValidateDocumentRequest, skipBack?: boolean }) =>
        this.covidAssessmentService.validate(action.document.docId, action.request).pipe(
          switchMap((response) => {
            if (action?.skipBack) {
              return [
                CovidAssessmentActions.validateCovidAssessmentSuccess(),
                PtCoreActions.showOperationSuccessNotification()
              ];
            } else {
              return [
                CovidAssessmentActions.validateCovidAssessmentSuccess(),
                PtCoreActions.navigateBack({
                  hop: -1,
                  queryParams: {
                    opened: true,
                    id: action.document.docId
                  }
                }),
                PtCoreActions.showOperationSuccessNotification()
              ];
            }
          }),
          catchError(err =>
            of(CovidAssessmentActions.validateCovidAssessmentFailed({ error: err.message }))
          )
        )
      )
    );
  });
  validateDocumentWithConfirm$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(CovidAssessmentActions.validateCovidAssessmentWithConfirm),
        map((action: { document: UiDocumentItem, skipBack?: boolean }) => {
          const title = 'TITLE.COVID_ASSESSMENT_VALIDATE';
          const message = 'MESSAGE.COVID_ASSESSMENT_VALIDATE';
          return this.dialogService.runValidateRequestDialog(title, message
          ).subscribe((request) => {
            if (request) {
              this.store.dispatch(CovidAssessmentActions.validateCovidAssessment({
                document: action?.document,
                request,
                skipBack: action.skipBack
              }));
            } else {
              this.store.dispatch(CovidAssessmentActions.validateCovidAssessmentFailed({ error: null }));
            }
          });
        })
      );
    }, { dispatch: false }
  );
  reopenCovidAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CovidAssessmentActions.reopenCovidAssessment),
      switchMap((action) =>
        this.covidAssessmentService.reopen(action.id).pipe(
          switchMap(() => [
              PtCoreActions.showOperationSuccessNotification(),
              CovidAssessmentActions.reopenCovidAssessmentSuccess({ id: action.id })
            ]
          ),
          catchError(err =>
            of(CovidAssessmentActions.reopenCovidAssessmentFailed({ error: err.message }))
          )
        )
      )
    );
  });
  reopenCovidAssessmentWithConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CovidAssessmentActions.reopenCovidAssessmentWithConfirm),
      switchMap((action) => {
        const title = 'TITLE.COVID_ASSESSMENT_REOPEN';
        const message = 'MESSAGE.COVID_ASSESSMENT_REOPEN';

        return this.widgetDialogService.runConfirmDialog(title, message).pipe(
          map((response) => {
            if (response) {
              this.store.dispatch(CovidAssessmentActions.reopenCovidAssessment({
                id: action?.id
              }));
            }
          })
        );
      })
    ), { dispatch: false }
  );
  updateCovidAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CovidAssessmentActions.updateCovidAssessment),
      switchMap((action: { id: number, request: CovidAssessmentDocUpdateRequest }) =>
        this.covidAssessmentService.update(action.id, action.request).pipe(
          map((response: UpdateDocResponse) =>
            CovidAssessmentActions.updateCovidAssessmentSuccess({ response })
          ),
          catchError(err =>
            of(CovidAssessmentActions.updateCovidAssessmentFailed({ error: err.message }))
          )
        )
      )
    );
  });
  renameCovidAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CovidAssessmentActions.renameCovidAssessment),
      switchMap((action) =>
        this.covidAssessmentService.rename(action.id, action.request).pipe(
          map(() =>
            CovidAssessmentActions.renameCovidAssessmentSuccess({ id: action.id })
          ),
          catchError(err =>
            of(CovidAssessmentActions.renameCovidAssessmentFailed({ error: err.message }))
          )
        )
      )
    );
  });
  createCovidAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CovidAssessmentActions.createCovidAssessment),
      switchMap((action: { request: CovidAssessmentDocCreateRequest }) =>
        this.covidAssessmentService.create(action.request).pipe(
          map((response: CreateDocResponse) =>
            CovidAssessmentActions.createCovidAssessmentSuccess({ id: response.docId })
          ),
          catchError(err =>
            of(CovidAssessmentActions.createCovidAssessmentFailed({ error: err.message }))
          )
        )
      )
    );
  });

  // updateCovidAssessmentSuccess$ = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(CovidAssessmentActions.updateCovidAssessmentSuccess),
  //     switchMap((action: { response: UpdateDocResponse }) =>
  //       this.covidAssessmentService.getTemplate(action.response.docId).pipe(
  //         map((response: TemplateWithUser) =>
  //           WidgetActions.updateStoreTemplate({
  //             template: response as CovidAssessmentEditable,
  //             docType: DocumentEditorType.COVID_ASSESSMENT
  //           })
  //         ),
  //         catchError(() =>
  //           of(WidgetActions.updateStoreTemplate({template: null, docType: null}))
  //         )
  //       )
  //     )
  //   );
  // });
  moveCovidAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CovidAssessmentActions.moveCovidAssessment),
      switchMap((action: { id: number, workspaceMoveRequest: WorkspaceMoveRequest }) =>
        this.workspaceService.move(action.id, WorkspaceItemType.COVID_ASSESSMENT, action.workspaceMoveRequest).pipe(
          map((item: WorkspaceItem) =>
            CovidAssessmentActions.moveCovidAssessmentSuccess({ item })
          ),
          catchError(err => of(
            CovidAssessmentActions.moveCovidAssessmentFailed({ error: err.message })
          ))
        )
      )
    );
  });
  duplicateCovidAssessment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CovidAssessmentActions.duplicateCovidAssessment),
      switchMap((action) =>
        this.covidAssessmentService.duplicate(action.id, action.request).pipe(
          map((response: DocumentDuplicateResponse) => CovidAssessmentActions.duplicateCovidAssessmentSuccess({ response })),
          catchError(err => of(
            CovidAssessmentActions.duplicateCovidAssessmentFailed({
              error: err?.message
            })
          ))
        )
      )
    )
  );
  deleteCovidAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CovidAssessmentActions.deleteCovidAssessment),
      switchMap((action: { id: number }) =>
        this.covidAssessmentService.delete(action.id).pipe(
          map(() => CovidAssessmentActions.deleteCovidAssessmentSuccess()),
          catchError(err => of(
            CovidAssessmentActions.deleteCovidAssessmentFailed({
              error: err.message
            })
          ))
        )
      ));
  });

  constructor(
    private store: Store,
    private actions$: Actions,
    private authService: AuthService,
    private queryService: QueryService,
    private widgetDialogService: WidgetDialogService,
    private covidAssessmentService: CovidAssessmentsService,
    private workspaceService: WorkspaceService,
    private dialogService: DialogService,
    private translateService: TranslateService
  ) {
  }

  // deleteCovidAssessmentWithVersion$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(CovidAssessmentActions.deleteCovidAssessmentWithVersion),
  //     switchMap((action: { id: number}) => {
  //         const title = this.translateService.stream('TITLE.COVID_ASSESSMENT_DELETE');
  //         const message = this.translateService.stream('MESSAGE.COVID_ASSESSMENT_DELETE');
  //         return this.dialogService.runConfirmDialogWithValidation({
  //           title,
  //           message,
  //           dialogType: DialogType.DELETE,
  //           size: 'md'
  //         }).pipe(
  //           map((response) => {
  //             if (response) {
  //               this.covidAssessmentService.delete(action.id).subscribe(
  //                 (success) => {
  //                   this.store.dispatch(CovidAssessmentActions.deleteCovidAssessmentWithVersionSuccess());
  //                   this.store.dispatch(PtCoreActions.navigateBack({
  //                     hop: -1,
  //                     queryParams: {
  //                       opened: true,
  //                       id: action.id
  //                     }
  //                   }));
  //                   this.store.dispatch(PtCoreActions.showOperationSuccessNotification());
  //                 },
  //                 (error) => CovidAssessmentActions.deleteCovidAssessmentWithVersionFailed({
  //                   error: error.message
  //                 })
  //               )
  //             }
  //           })
  //         );
  //       }
  //     )
  //   ), {dispatch: false}
  // );
}
